import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { BsPlus } from 'react-icons/bs';
import { AiOutlineExpandAlt, AiOutlineFile } from 'react-icons/ai';
import FileViewerModal from './components/FileViewerModal';
import FileInputModal from './components/FileInputModal';
import FileSliderModal from './components/FileSliderModal';
import { colors, FILE_TYPES } from '../../constants';
import { useReduxActions } from 'hooks/useReduxActions';
import { getSmallFileUrl } from 'utils/helpers';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 10px;
`;

const FileImage = styled.img`
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
`;

const FileVideo = styled.video`
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
`;

const FileDocument = styled(AiOutlineFile)`
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
`;

const StyledAddFileButton = styled.button`
  background-color: ${colors.green60};
  border-radius: 4px;
  width: 24px;
  height: 24px;
`;

const StyledExpandButton = styled.button`
  background-color: ${colors.green};
  width: 16px;
  height: 16px;
  border-radius: 4px;
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileFormatter = ({ data, editable, type, rowId, recall, model }) => {
  const actions = useReduxActions();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenViewer, setIsOpenViewer] = useState(false);
  const [isOpenSlider, setIsOpenSlider] = useState(false);

  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  const handleCloseViewer = () => {
    setIsOpenViewer(false);
  };

  const handleCloseSlider = () => {
    setIsOpenSlider(false);
  };

  const handleOpenModal = () => {
    if (editable) {
      setIsOpenModal(true);
    }
  };

  const handleOpenViewer = () => {
    if (editable) {
      setIsOpenViewer(true);
    }
  };

  const handleOpenSlider = () => {
    setIsOpenSlider(true);
  };

  const handleDeleteFile = id => async event => {
    event.stopPropagation();
    try {
      await actions.deleteFile(model, id, rowId);
      recall();
    } catch (err) {
      console.error(err);
    }
  };

  const getFileSource = item => {
    switch (item.mimetype) {
      case FILE_TYPES.IMAGE:
        return (
          <FileImage
            key={item.id}
            src={getSmallFileUrl(item.url, 40)}
            onClick={handleOpenSlider}
            alt=""
          />
        );
      case FILE_TYPES.VIDEO:
        return (
          <FileVideo
            key={item.id}
            src={getSmallFileUrl(item.url, 40)}
            onClick={handleOpenSlider}
            type={item.mimetype}
          />
        );
      default:
        return <FileDocument key={item.id} onClick={handleOpenSlider} />;
    }
  };

  return (
    <>
      <StyledWrapper>
        {data?.map(item => getFileSource(item))}
        {!!data.length && (
          <StyledExpandButton onClick={handleOpenViewer}>
            <AiOutlineExpandAlt fill={colors.white} />
          </StyledExpandButton>
        )}
        {editable && (
          <StyledAddFileButton onClick={handleOpenModal}>
            <BsPlus fill={colors.green} />
          </StyledAddFileButton>
        )}
      </StyledWrapper>
      {isOpenModal && (
        <FileInputModal
          onClose={handleCloseModal}
          type={type}
          rowId={rowId}
          recall={recall}
          model={model}
        />
      )}
      {isOpenViewer && (
        <FileViewerModal
          data={data}
          onClose={handleCloseViewer}
          type={type}
          onAddMore={handleOpenModal}
          onFileClick={handleOpenSlider}
          onDeleteFile={handleDeleteFile}
        />
      )}
      {isOpenSlider && (
        <FileSliderModal
          data={data}
          onClose={handleCloseSlider}
          onDeleteFile={handleDeleteFile}
        />
      )}
    </>
  );
};

FileFormatter.propTypes = {
  data: PropTypes.array,
  editable: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(FILE_TYPES)),
  rowId: PropTypes.string,
  recall: PropTypes.func,
  model: PropTypes.string,
};

FileFormatter.defaultProps = {
  data: [],
};

export default FileFormatter;
